/**
 * useSEO — lightweight per-route meta injection + GA4 pageview tracking.
 * Usage inside any page component:
 *   useSEO({
 *     title: "Invest UAE Forum 2026 · SKI AXIS",
 *     description: "Online investment forum on 31 May 2026...",
 *     canonical: "/events/invest-uae-forum-2026",
 *     keywords: "UAE investment forum 2026, Dubai investment webinar",
 *     ogImage: "/og/forum.png",
 *     jsonLd: { ... } // optional structured data object or array
 *   });
 */
import { useEffect } from 'react';

const SITE_ORIGIN = 'https://skiaxis.com';

function setMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    // derive key attribute from selector
    const m = selector.match(/meta\[(\w+)=["']([^"']+)["']\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr || 'content', value);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

export function useSEO({ title, description, keywords, canonical, ogImage, ogType = 'website', jsonLd, noindex = false } = {}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta('meta[name="description"]', 'content', description);
    if (keywords) setMeta('meta[name="keywords"]', 'content', keywords);
    if (canonical) {
      const url = canonical.startsWith('http') ? canonical : SITE_ORIGIN + (canonical.startsWith('/') ? canonical : '/' + canonical);
      setLink('canonical', url);
      setMeta('meta[property="og:url"]', 'content', url);
    }
    setMeta('meta[property="og:type"]', 'content', ogType);
    if (title) {
      setMeta('meta[property="og:title"]', 'content', title);
      setMeta('meta[name="twitter:title"]', 'content', title);
    }
    if (description) {
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }
    if (ogImage) {
      const img = ogImage.startsWith('http') ? ogImage : SITE_ORIGIN + (ogImage.startsWith('/') ? ogImage : '/' + ogImage);
      setMeta('meta[property="og:image"]', 'content', img);
      setMeta('meta[name="twitter:image"]', 'content', img);
    }
    if (noindex) setMeta('meta[name="robots"]', 'content', 'noindex, nofollow');
    else setMeta('meta[name="robots"]', 'content', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');

    // Structured data (allows array of JSON-LD objects)
    const list = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    list.forEach((data, i) => upsertJsonLd(`seo-jsonld-${i}`, data));
    // Cleanup extras on re-run
    let k = list.length;
    while (document.getElementById(`seo-jsonld-${k}`)) { removeJsonLd(`seo-jsonld-${k}`); k++; }

    // GA4 pageview
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      try {
        window.gtag('event', 'page_view', {
          page_title: title || document.title,
          page_location: window.location.href,
          page_path: window.location.pathname + window.location.search,
        });
      } catch (_e) { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, canonical, ogImage, ogType, noindex, JSON.stringify(jsonLd)]);
}

/** Fire a GA4 custom event (CTA click, form submit, etc.) */
export function trackEvent(name, params = {}) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try { window.gtag('event', name, params); } catch (_e) { /* noop */ }
  }
  // Also forward to PostHog if loaded (posthog is already present on this platform)
  if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.capture === 'function') {
    try { window.posthog.capture(name, params); } catch (_e) { /* noop */ }
  }
  // Forward to Umami Cloud (privacy-friendly, GDPR-safe analytics)
  if (typeof window !== 'undefined' && window.umami && typeof window.umami.track === 'function') {
    try { window.umami.track(name, params); } catch (_e) { /* noop */ }
  }
}

export default useSEO;
